import { ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WizardService } from '../../services/wizard.service';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, map, Observable, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { SponsorDialogComponent } from '../sponsor-dialog/sponsor-dialog.component';
import { LeadApi } from '../../api/leadApi';
import { EducationalInstitution } from '../../models/educational_institution';
import { ActivatedRoute, Router } from '@angular/router';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { ErrorConstants } from '../../constants/error_constants';
import { ErrorDialogComponent } from '../error-dialog/error-dialog.component';
import { environment } from '../../../environments/environment';

export function RequireMatch(control: AbstractControl) {
  const selection: any = control.value;
  if (typeof selection === "string") {
    return { incorrect: true };
  }
  return null;
}

@Component({
  selector: 'app-wizard',
  standalone: false,
  templateUrl: './wizard.component.html',
  styleUrl: './wizard.component.scss',
})
export class WizardComponent implements OnInit, OnDestroy {

  submitted = signal(false);

  public wizardForm!: FormGroup;

  private sponsorOriginalValidators: { [key: string]: any } = {};

  public is_loading: boolean = true;

  public is_ei_unique_link: boolean = false;

  public isEIGroup: boolean = false;

  public eiLoading: boolean = false;

  readonly dialog = inject(MatDialog);

  destroy$: Subject<boolean> = new Subject<boolean>();
  public isRecommendedInstitution: boolean = false;
  public educationalInstitutions: EducationalInstitution[] = [];
  public filteredEIS!: Observable<EducationalInstitution[]> | undefined;
  public filter_partner_school: boolean = false;
  @ViewChild('educationalInstitution') educationalInstitution: ElementRef | undefined;

  private lead_id: any = null;
  private backgroundLeadCreated: boolean = true;
  env: any = environment;


  @ViewChild(MatAutocompleteTrigger)
  autocomplete!: MatAutocompleteTrigger;
  utm_source: string = '';


  constructor(
    private fb: FormBuilder,
    private wizardService: WizardService,
    private leadApi: LeadApi,
    private route: ActivatedRoute,
    private recaptchaV3Service: ReCaptchaV3Service,
    private cdr: ChangeDetectorRef,
    private router: Router,

  ) {

    this.wizardForm = this.fb.group({
      student_name: ['', Validators.required],
      student_surname: ['', Validators.required],
      student_id_number: ['', [Validators.required, this.wizardService.verifyid_number]],
      student_email: ['', [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)]],
      student_contact_number: ['', [Validators.required, Validators.pattern(/^(\+?\d{1,3}[- ]?)?\d{10}$/)]],
      educational_institution: ['', [Validators.required]],
      educational_institution_name: [],
      educational_institution_contact_email: ['', Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)],
      educational_institution_contact_number: ['', Validators.pattern(/^(\+?\d{1,3}[- ]?)?\d{10}$/)],
      educational_institution_website: [],
      student_number: [''],
      course_fees: [null, [Validators.required, Validators.min(2000), Validators.max(350000)]],
      payment_made_by: [null, Validators.required],
      terms: [false, Validators.requiredTrue],

      sponsor: this.fb.group({
        sponsor_name: ['', Validators.required],
        sponsor_surname: ['', Validators.required],
        sponsor_id_number: ['', [Validators.required, this.wizardService.verifyid_number, this.wizardService.under18Validator]],
        sponsor_email: ['', [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)]],
        sponsor_contact_number: ['', [Validators.required, Validators.pattern(/^(\+?\d{1,3}[- ]?)?\d{10}$/)]],
      })
    });
  }



  ngOnInit() {
    window.scrollTo(0, 0);
    sessionStorage.clear();
    const sponsorGroup = this.wizardForm.get('sponsor') as FormGroup;
    if (sponsorGroup) {
      Object.keys(sponsorGroup.controls).forEach(key => {
        this.sponsorOriginalValidators[key] = sponsorGroup.get(key)?.validator;
      });
    }

    // Educational Institution
    const unique_id = this.route.snapshot.paramMap.get('unique_id') || sessionStorage.getItem('unique_id');
    if (unique_id) {
      sessionStorage.setItem('unique_id', unique_id);
      this.getUniqueLinkEducationalInstitution(unique_id);
    }

    this.filteredEIS = this.wizardForm.get('educational_institution')!.valueChanges.pipe(
      debounceTime(1000),
      startWith(''),
      switchMap(val => {
        return this._filter(val || '');
      })
    );


    this.utm_source = this.route.snapshot.queryParamMap.get('utm_source') || sessionStorage.getItem('utm_source') || '';

    sessionStorage.setItem('utm_source', this.utm_source);


    if (sessionStorage.getItem('lead_id')) {
      this.lead_id = sessionStorage.getItem('lead_id');
    }

    this.wizardForm
      .get('payment_made_by')
      ?.valueChanges.subscribe(value => {
        this.paymentMadeByChanged(value);
      });

    this.is_loading = false;
  }

  private _filter(value: string): Observable<any[]> {
    if (typeof value !== 'string') {
      return new Observable<any[]>();
    }

    return this.leadApi.searchEducationalInstitutions(value, this.filter_partner_school)
      .pipe(
        takeUntil(this.destroy$),
        map((res: any) => {
          if (res.data.length === 0) {
            this.educationalInstitutions = [{ id: 0, education_group: { id: 0, unique_id: 0, name: '' }, unique_id: '0', ei_unique_id: '0', name: 'No results found', contact_email: '', recommended_institution: false, contact_number: '', website: '' }];
          } else if (res.success) {
            this.educationalInstitutions = res.data;
          }
          else {
            console.error('Error fetching educational institutions:', res.message);
          }
          this.eiLoading = false;
          return this.educationalInstitutions;
        })
      );
  }

  // Unique link single EI
  private getUniqueLinkEducationalInstitution(ei_unique_id: any) {
    this.leadApi.getUniqueLinkEducationalInstitution(ei_unique_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.wizardForm.patchValue({
              educational_institution: res.data
            });
            this.wizardForm.get('educational_institution')?.disable();
            this.is_ei_unique_link = true;
          } else {
            this.leadApi.getEducationalInstitutionsFromGroup(ei_unique_id)
              .pipe(takeUntil(this.destroy$))
              .subscribe((res: any) => {
                if (res.data.length > 0) {
                  this.educationalInstitutions = res.data;
                  this.isEIGroup = true;
                }
              });
          }
        },
        error: (error) => {
          console.error('Error fetching educational institution:', error);
        },
      });
  };


  private toggleSponsorValidation(enable: boolean) {
    const sponsorGroup = this.wizardForm.get('sponsor') as FormGroup;

    if (!sponsorGroup) return;

    Object.keys(sponsorGroup.controls).forEach(key => {
      const control = sponsorGroup.get(key);
      if (!control) return;

      if (enable) {
        // Merge original validators with required
        const original = this.sponsorOriginalValidators[key];
        control.setValidators([Validators.required, original].filter(Boolean));
      } else {
        // Disable validation
        control.clearValidators();
        control.reset();
      }

      control.updateValueAndValidity();
    });
  }

  showError(form: FormGroup, controlName: string): boolean {
    const control = form.get(controlName);
    return !!(
      control &&
      control.invalid &&
      (control.touched || this.submitted())
    );
  }

  isStudentSectionValid(): boolean {
    const controlsToCheck = [
      'student_name',
      'student_surname',
      'student_id_number',
      'student_email',
      'student_contact_number',
      'educational_institution',
      'student_number',
      'course_fees',
    ];

    return controlsToCheck.every(controlName => {
      const control = this.wizardForm.get(controlName);
      return control?.valid === true;
    });
  }


  createBackgroundLead() {
    if (!this.lead_id && this.isStudentSectionValid()) {
      try {
        this.recaptchaV3Service.execute('CreatingLead')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (token: any) => {

              const leadData = {
                student: this.getStudentData(),
                educational_institution: this.isRecommendedInstitution ? this.getRecommendedEI() : this.wizardForm.get('educational_institution')?.value,
                applicant: undefined,
                student_responsible_for_payment: this.wizardForm.get('payment_made_by')?.value,
                requested_amount: this.wizardForm.get('course_fees')?.value,
                utm_source: this.utm_source,
                recaptcha_token: token,
                terms_of_use_accepted: this.wizardForm.get('terms')?.value
              }

              this.leadApi.createLead(leadData)
                .pipe(takeUntil(this.destroy$))
                .subscribe((response: any) => {
                  if (response.success) {
                    this.lead_id = response.data.identifier;
                    sessionStorage.setItem('lead_id', response.data.identifier);
                  } else {
                    this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.CREATE_LEAD);
                  }
                  this.is_loading = false;
                });
            },
            error: (error) => {
              this.is_loading = false;
              console.log('Error creating lead', error);
              this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.RECAPTCHA);
            }
          });
      } catch (error) {
        this.is_loading = false;
        console.log('Error creating lead', error);
        this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.CREATE_LEAD);
      }
      finally {
        this.is_loading = false;
      }
    }
  }

  createLead() {
    this.is_loading = true;
    if (!this.formsValid()) {
      try {

        this.recaptchaV3Service.execute('CreatingLead')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (token: any) => {
              const leadData = {
                student: this.getStudentData(),
                educational_institution: this.isRecommendedInstitution ? this.getRecommendedEI() : this.wizardForm.get('educational_institution')?.value,
                applicant: this.getApplicantData(),
                student_responsible_for_payment: this.wizardForm.get('payment_made_by')?.value,
                requested_amount: this.wizardForm.get('course_fees')?.value,
                utm_source: this.utm_source,
                recaptcha_token: token,
                terms_of_use_accepted: this.wizardForm.get('terms')?.value
              }

              this.leadApi.createLead(leadData)
                .pipe(takeUntil(this.destroy$))
                .subscribe((response: any) => {
                  if (response.success) {
                    this.lead_id = response.data.identifier;
                    sessionStorage.setItem('lead_id', response.data.identifier);
                    // Redirect to success page
                    sessionStorage.clear();
                    window.location.href = environment.url + `lead/absa/redirect/${this.lead_id}/`;

                  } else {
                    console.log('Lead creation failed');
                    this.is_loading = false;
                  }
                });
            },
            error: (error) => {
              this.is_loading = false;
              console.log('Error creating lead', error);
              this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.RECAPTCHA);
            }
          });
      } catch (error) {
        this.is_loading = false;
        console.log('Error creating lead', error);
        this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.CREATE_LEAD);
      }

    } else {
      this.wizardForm.markAllAsTouched();
      this.is_loading = false;
    }
  }


  formsValid(): boolean {
    var valid = true;
    if (this.wizardForm.valid) {
      valid = false;
    } else if (this.wizardForm.valid && !this.showSponsorForm) {
      valid = false;
    } else if (this.wizardForm.invalid) {
      valid = true;
    }

    return valid;
  }

  updateLead() {
    this.is_loading = true;
    if (!this.formsValid()) {
      try {
        this.recaptchaV3Service.execute('UpdateLead')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (token: any) => {

              const leadData = {
                student: this.getStudentData(),
                educational_institution: this.isRecommendedInstitution ? this.getRecommendedEI() : this.wizardForm.get('educational_institution')?.value,
                applicant: this.getApplicantData(),
                student_responsible_for_payment: this.wizardForm.get('payment_made_by')?.value,
                requested_amount: this.wizardForm.get('course_fees')?.value,
                utm_source: this.utm_source,
                identifier: this.lead_id,
                recaptcha_token: token,
                terms_of_use_accepted: this.wizardForm.get('terms')?.value,
                openSponsorDialog: false,
              }
              this.leadApi.updateLead(leadData, this.lead_id)
                .pipe(takeUntil(this.destroy$))
                .subscribe((response: any) => {
                  if (response.success) {
                    sessionStorage.clear();
                    // Redirect to success page
                    console.log(response.data.redirect_link);
                    window.location.href = environment.url + `lead/absa/redirect/${this.lead_id}/`;

                  } else {
                    console.log('Lead update failed');
                    this.is_loading = false
                  }

                });
            }, error: (error) => {

              this.is_loading = false;
              console.log('Error updating lead', error);
              this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.RECAPTCHA);
            }
          });
      } catch (error) {
        this.is_loading = false;
        console.log('Error updating lead', error);
        this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.UPDATE_LEAD);
      }
    }
    else {
      this.wizardForm.markAllAsTouched();
      this.is_loading = false;
    }
  }

  onSubmit() {
    this.submitted.set(true);

    if (this.lead_id) {
      console.log('Update lead');
      this.updateLead();
    }
    else {
      console.log('Create lead');
      this.createLead();
    }
  }

  paymentMadeByChanged(payment: 'student' | 'sponsor') {
    const studentId = this.wizardForm.get('student_id_number');
    const sponsorGroup = this.wizardForm.get('sponsor') as FormGroup;
    const sponsorId = sponsorGroup.get('sponsor_id_number');

    // Reset validators
    if (payment === 'student') {
      studentId?.addValidators(this.wizardService.under18Validator);
      studentId?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      // Remove under18 from sponsor if previously added
      sponsorId?.clearValidators();
      sponsorId?.setValidators([Validators.required, this.wizardService.verifyid_number]);
    } else if (payment === 'sponsor') {
      sponsorId?.addValidators(this.wizardService.under18Validator);
      studentId?.clearValidators();
      studentId?.setValidators([Validators.required, this.wizardService.verifyid_number]);
    }

    // Force Angular to re-run validation on **existing values**
    studentId?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    sponsorId?.updateValueAndValidity({ onlySelf: true, emitEvent: true });

    if (this.backgroundLeadCreated) {
      this.createBackgroundLead();
      this.backgroundLeadCreated = false;
    }

  }


  showSponsorForm(): boolean {
    if (this.wizardForm.get('payment_made_by')?.value === 'sponsor') {
      this.toggleSponsorValidation(true);
    } else {
      this.toggleSponsorValidation(false);
    };
    return this.wizardForm.get('payment_made_by')?.value === 'sponsor';
  }

  validateFormControl(controlName: string) {
    return this.wizardForm.get(controlName)?.invalid && this.wizardForm.get(controlName)?.touched;
  }

  validateNumericInput(event: KeyboardEvent): void {
    const charCode = event.charCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  validationMessage(controlName: string): string {

    let control;
    control = this.wizardForm.get(controlName);

    if (controlName === 'sponsor.sponsor_id_number' || controlName === 'student_id_number') {
      if (control?.hasError('id_number')) {
        return 'Enter a valid 13 digit South African ID number'
      }
    }

    if (control?.hasError('required')) {
      return 'This field is required';
    }

    if (controlName === 'sponsor.sponsor_email' || controlName === 'student_email' || controlName === 'educational_institution_contact_email') {
      if (control?.hasError('pattern')) {
        return 'Enter a valid email address';
      }
    }

    if (control?.hasError('minlength')) {
      return 'This field must be 13 characters';
    }

    if (control?.hasError('maxlength')) {
      return 'This field must be 13 characters';
    }

    if (control?.hasError('pattern')) {
      return 'Enter a valid 10 digit contact number, for example 0823334444';
    }

    if (control?.hasError('incorrect')) {
      return 'Please select an educational institution from the list';
    }

    if (control?.hasError('min')) {
      return 'Enter an amount above R2 000';
    }

    if (control?.hasError('max')) {
      return 'Enter an amount up to R350 000';
    }

    if (control?.hasError('under18')) {
      return 'Applicants need to be over the age of 18';
    }

    return 'This field is required';
  }

  clearValuesForRecommendedEI() {

    this.wizardForm.get('educational_institution_name')?.clearValidators();
    this.wizardForm.get('educational_institution_contact_email')?.clearValidators();
    this.wizardForm.get('educational_institution_contact_number')?.clearValidators();
    this.wizardForm.get('educational_institution_website')?.clearValidators();

    this.wizardForm.get('educational_institution_name')?.updateValueAndValidity();
    this.wizardForm.get('educational_institution_contact_email')?.updateValueAndValidity();
    this.wizardForm.get('educational_institution_contact_number')?.updateValueAndValidity();
    this.wizardForm.get('educational_institution_website')?.updateValueAndValidity();

    this.wizardForm.get('educational_institution_name')?.markAsPristine();
    this.wizardForm.get('educational_institution_contact_email')?.markAsPristine();
    this.wizardForm.get('educational_institution_contact_number')?.markAsPristine();
    this.wizardForm.get('educational_institution_website')?.markAsPristine();

    this.wizardForm.get('educational_institution_name')?.markAsUntouched();
    this.wizardForm.get('educational_institution_contact_email')?.markAsUntouched();
    this.wizardForm.get('educational_institution_contact_number')?.markAsUntouched();
    this.wizardForm.get('educational_institution_website')?.markAsUntouched();
  }

  recommendedInstitution() {
    this.autocomplete.closePanel();
    this.isRecommendedInstitution = true;

    this.clearValuesForRecommendedEI();

    this.wizardForm.get('educational_institution')?.clearValidators();
    this.wizardForm.get('educational_institution')?.updateValueAndValidity();

    this.wizardForm.get('educational_institution_name')?.addValidators(Validators.required);
    this.wizardForm.get('educational_institution_name')?.updateValueAndValidity();

    this.wizardForm.get('educational_institution_contact_email')?.addValidators([Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)]);
    this.wizardForm.get('educational_institution_contact_email')?.updateValueAndValidity();

    this.wizardForm.get('educational_institution_contact_number')?.addValidators([Validators.required, Validators.pattern(/^(\+?\d{1,3}[- ]?)?\d{10}$/)]);
    this.wizardForm.get('educational_institution_contact_number')?.updateValueAndValidity();

    this.wizardForm.get('educational_institution_website')?.addValidators([Validators.required]);
    this.wizardForm.get('educational_institution_website')?.updateValueAndValidity();
  }

  getRecommendedEI(): EducationalInstitution {
    return {
      id: 0,
      education_group: { id: 0, unique_id: 0, name: '' },
      unique_id: '0',
      ei_unique_id: '0',
      name: this.wizardForm.get('educational_institution_name')?.value,
      contact_email: this.wizardForm.get('educational_institution_contact_email')?.value,
      recommended_institution: true,
      contact_number: this.wizardForm.get('educational_institution_contact_number')?.value,
      website: this.wizardForm.get('educational_institution_website')?.value,
    }
  }

  getEiName(ei: any): string {
    return ei ? ei.name : '';
  }

  getStudentData(): any {
    return {
      first_name: this.wizardForm.get('student_name')?.value,
      last_name: this.wizardForm.get('student_surname')?.value,
      id_number: this.wizardForm.get('student_id_number')?.value,
      email: this.wizardForm.get('student_email')?.value,
      contact_number: this.wizardForm.get('student_contact_number')?.value,
      student_number: this.wizardForm.get('student_number')?.value,
    }
  }

  getApplicantData(): any {
    if (this.showSponsorForm()) {
      return {
        first_name: this.wizardForm.get('sponsor.sponsor_name')?.value,
        last_name: this.wizardForm.get('sponsor.sponsor_surname')?.value,
        id_number: this.wizardForm.get('sponsor.sponsor_id_number')?.value,
        email: this.wizardForm.get('sponsor.sponsor_email')?.value,
        contact_number: this.wizardForm.get('sponsor.sponsor_contact_number')?.value,
      }
    } else {
      return this.getStudentData();
    }
  }

  scrollToInstitution(): void {
    if (this.educationalInstitution && window.innerWidth <= 800) {
      this.educationalInstitution.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }



  openErrorDialog(title: string, message: string, errorCode: string) {
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      data: {
        title: title,
        message: message,
        errorCode: errorCode
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        console.log(`Dialog result: ${result}`);
      });
  }


  openSponsorDialog() {
    this.is_loading = true;

    if (!this.formsValid()) {

      try {
        this.recaptchaV3Service.execute('UpdateLead')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (token: any) => {

              const leadData = {
                student: this.getStudentData(),
                educational_institution: this.isRecommendedInstitution ? this.getRecommendedEI() : this.wizardForm.get('educational_institution')?.value,
                applicant: this.getApplicantData(),
                student_responsible_for_payment: this.wizardForm.get('payment_made_by')?.value,
                requested_amount: this.wizardForm.get('course_fees')?.value,
                utm_source: this.utm_source,
                identifier: this.lead_id,
                recaptcha_token: token,
                terms_of_use_accepted: this.wizardForm.get('terms')?.value,
                openSponsorDialog: false,
              }

              if (this.lead_id) {
                this.leadApi.updateLead(leadData, this.lead_id)
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((response: any) => {
                    if (response.success) {
                      console.log('Lead updated successfully');
                      sessionStorage.clear();

                      Promise.resolve().then(() => {
                        const dialogRef = this.dialog.open(SponsorDialogComponent, {
                          data: {
                            lead: response.data,
                            redirectUrl: response.data.redirect_link,
                            recaptcha_token: token,
                          }
                        });

                        dialogRef.afterClosed()
                          .pipe(takeUntil(this.destroy$))
                          .subscribe(result => {
                            console.log(`Dialog result: ${result}`);
                            this.is_loading = false;
                            this.cdr.detectChanges();

                            if (result === 'go_to_link') {
                              this.router.navigate(['/']);
                            }
                          });
                      });

                    } else {
                      console.log('Lead update failed');
                    }
                    this.is_loading = false

                  });
              } else {
                this.leadApi.createLead(leadData)
                  .pipe(takeUntil(this.destroy$))
                  .subscribe((response: any) => {
                    Promise.resolve().then(() => {
                      const dialogRef = this.dialog.open(SponsorDialogComponent, {
                        data: {
                          lead: response.data,
                          redirectUrl: response.data.redirect_link,
                          recaptcha_token: token,
                        }
                      });

                      dialogRef.afterClosed()
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(result => {
                          console.log(`Dialog result: ${result}`);
                          this.is_loading = false;
                          this.cdr.detectChanges();
                          if (result === 'go_to_link') {
                            this.router.navigate(['/']);
                          }
                        });
                    });
                  });
              }
            }, error: (error) => {

              this.is_loading = false;
              console.log('Error updating lead', error);
              this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.RECAPTCHA);
            }
          });
      } catch (error) {
        this.is_loading = false;
        console.log('Error updating lead', error);
        this.openErrorDialog('Error', 'An error occurred. Please try again later.', ErrorConstants.UPDATE_LEAD);
      }

    } else {
      this.wizardForm.markAllAsTouched();
      console.log('Forms are invalid');
      this.is_loading = false;
    }
  }

  fillDummyData() {
    console.log('Filling dummy data');
    this.wizardForm.patchValue({

      student_name: 'Oscar',
      student_surname: 'Piastri',
      student_id_number: '9605175060083',
      student_email: 'john.doe@example.com',
      student_contact_number: '0825361078',
      student_number: 'S1234567',
      sponsor: {
        sponsor_name: 'Kamala',
        sponsor_surname: 'Khan',
        sponsor_id_number: '9605175060083',
        sponsor_email: 'jane.doe@example.com',
        sponsor_contact_number: '0987654321',
      },
      payment_made_by: 'sponsor',
      educational_institution: undefined,
      course_fees: 25000,
      terms_of_use_accepted: true,

    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }


}


